import { type ReactElement } from 'react';
import { FilterChips } from '@/components/filter-chips';
import { Button } from '@/components/atoms/button';
import { Icon } from '@/components/atoms/icon';
import { Micro } from '@/components/atoms/micro';
import { RepertoireList } from '@/components/organisms/repertoire-list';
import { RepertoireDetailPanel } from '@/components/organisms/repertoire-detail-panel';
import type { RepertoireEntry } from '@/lib/types';

const LIST_WIDTH_PX = 420;

type SortValue = `${'name' | 'mastery' | 'queueStatus'}-${'asc' | 'desc'}`;

const SORT_OPTIONS: { value: SortValue; label: string }[] = [
  { value: 'name-asc',         label: 'Name A–Z' },
  { value: 'name-desc',        label: 'Name Z–A' },
  { value: 'mastery-asc',      label: 'Mastery (low first)' },
  { value: 'mastery-desc',     label: 'Mastery (high first)' },
  { value: 'queueStatus-asc',  label: 'Queue status' },
];

type FilterChipOption = { id: string; label: string };

type Props = {
  entries: RepertoireEntry[];
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  masterySortOrderById: Map<string, number>;
  search: string;
  onSearchChange: (value: string) => void;
  queueChipOptions: FilterChipOption[];
  queueStatusFilter: string | null;
  onQueueStatusChange: (id: string | null) => void;
  masteryChipOptions: FilterChipOption[];
  masteryFilter: string | null;
  onMasteryChange: (id: string | null) => void;
  sortValue: SortValue;
  onSortChange: (value: SortValue) => void;
  selectedArrangementId: string | null;
  onSelect: (arrangementId: string) => void;
};

export function RepertoireSplitPane({
  entries,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  masterySortOrderById,
  search,
  onSearchChange,
  queueChipOptions,
  queueStatusFilter,
  onQueueStatusChange,
  masteryChipOptions,
  masteryFilter,
  onMasteryChange,
  sortValue,
  onSortChange,
  selectedArrangementId,
  onSelect,
}: Props): ReactElement {
  return (
    <div
      data-repertoire-split-pane
      className="flex h-[calc(100vh-0px)] min-h-0 bg-[var(--bg-page)]"
    >
      <aside
        data-repertoire-list-column
        data-list-width={String(LIST_WIDTH_PX)}
        style={{ width: `${LIST_WIDTH_PX}px` }}
        className="shrink-0 border-r border-[var(--rule)] flex flex-col bg-[var(--bg-surface)]"
      >
        <div className="p-4 border-b border-[var(--rule)] space-y-3">
          <div className="flex h-11 items-center gap-2 rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-soft)] px-3 transition-colors focus-within:border-[var(--ink)]">
            <Icon name="search" size={15} className="shrink-0 text-[var(--ink-3)]" />
            <input
              type="text"
              placeholder="Search repertoire…"
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              className="flex-1 bg-transparent text-[14px] text-[var(--ink)] placeholder-[var(--ink-4)] outline-none"
            />
          </div>

          {queueChipOptions.length > 0 && (
            <div>
              <Micro className="mb-1.5 block text-[var(--ink-3)]">Queue status</Micro>
              <FilterChips
                options={queueChipOptions}
                selected={queueStatusFilter}
                onSelect={onQueueStatusChange}
              />
            </div>
          )}

          {masteryChipOptions.length > 0 && (
            <div>
              <Micro className="mb-1.5 block text-[var(--ink-3)]">Mastery level</Micro>
              <FilterChips
                options={masteryChipOptions}
                selected={masteryFilter}
                onSelect={onMasteryChange}
              />
            </div>
          )}

          <div className="flex items-center justify-between">
            <Micro className="text-[var(--ink-3)]">Sort by</Micro>
            <select
              value={sortValue}
              onChange={(event) => onSortChange(event.target.value as SortValue)}
              className="h-9 rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-surface)] px-3 text-[12px] font-semibold text-[var(--ink)] focus:border-[var(--ink)] focus:outline-none"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto nice-scroll p-3">
          {isLoading && <ListSkeleton />}
          {!isLoading && (
            <>
              <RepertoireList
                mode="select"
                entries={entries}
                masterySortOrderById={masterySortOrderById}
                selectedArrangementId={selectedArrangementId}
                onSelect={onSelect}
                density="single-column"
              />
              {hasNextPage && (
                <div className="flex justify-center pt-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={isFetchingNextPage}
                    aria-busy={isFetchingNextPage || undefined}
                    onClick={() => fetchNextPage()}
                  >
                    {isFetchingNextPage ? 'Loading more…' : 'Load more'}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {selectedArrangementId
          ? <RepertoireDetailPanel arrangementId={selectedArrangementId} />
          : <SplitPanePlaceholder />}
      </div>
    </div>
  );
}

const SKELETON_ROW_COUNT = 6;

function ListSkeleton(): ReactElement {
  return (
    <div className="space-y-2">
      {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
        <div
          key={i}
          className="animate-pulse rounded-[var(--r-md)] border border-[var(--rule)] bg-[var(--bg-surface)] p-4"
        >
          <div className="h-4 w-1/3 rounded bg-[var(--bg-soft)]" />
          <div className="mt-2 h-3 w-1/4 rounded bg-[var(--bg-soft)]" />
        </div>
      ))}
    </div>
  );
}

function SplitPanePlaceholder(): ReactElement {
  return (
    <div
      data-repertoire-detail-panel
      className="flex flex-1 items-center justify-center text-sm text-[var(--ink-3)]"
    >
      Select an arrangement to preview.
    </div>
  );
}
